import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { informeService } from '../services/api';
import { InformePericial, User } from '../types';
import { FiSearch, FiFileText, FiInbox, FiTrash2, FiFolder, FiPlus } from 'react-icons/fi';
import './InformesList.css';
export interface InformesListProps {
  user: User;
}

interface DeleteModal {
  show: boolean;
  id: number | null;
  numero: string;
}

const InformesList: React.FC<InformesListProps> = () => {
  const [informes, setInformes] = useState<InformePericial[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const [filtros, setFiltros] = useState({ numero_siniestro: '', numero_poliza: '' });
  const [filtrando, setFiltrando] = useState<boolean>(false);

  const [syncingGmail, setSyncingGmail] = useState<boolean>(false);
  const [syncMessage, setSyncMessage] = useState<string>('');
  const [syncProgress, setSyncProgress] = useState<{ current: number; total: number; message: string } | null>(null);

  const [deleteModal, setDeleteModal] = useState<DeleteModal>({ show: false, id: null, numero: '' });
  const [deleting, setDeleting] = useState<boolean>(false);

  useEffect(() => {
    prvCargarInformes();
  }, []);

  const prvCargarInformes = async () => {
    try {
      setLoading(true);
      const response = await informeService.listarInformes();
      setInformes(response.data);
    } catch (err: any) {
      console.error('Error al cargar informes:', err);
      setError(err.response?.data?.error || 'Error al cargar los informes');
    } finally {
      setLoading(false);
    }
  };

  const prvHandleSincronizarGmail = async () => {
    try {
      setSyncingGmail(true);
      setSyncMessage('');
      setError('');
      setSyncProgress({ current: 0, total: 0, message: 'Conectando con Gmail...' });

      try {
        await informeService.sincronizarGmailProgress((progress) => {
          setSyncProgress({
            current: progress.current,
            total: progress.total,
            message: progress.message
          });
          if (progress.completed && progress.message) {
            setSyncMessage(progress.message);
          }
        });
      } catch (streamErr: any) {
        console.warn('Streaming no disponible, usando sincronización estándar:', streamErr);
        const response = await informeService.sincronizarGmail();
        if (response.data.success) {
          setSyncMessage(`Sincronización completada: ${response.data.message}`);
        } else {
          setError(response.data.message || 'Error al sincronizar con Gmail');
        }
      }

      await prvCargarInformes();
    } catch (err: any) {
      console.error('Error al sincronizar con Gmail:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || err.message || 'Error al conectar con Gmail');
    } finally {
      setSyncingGmail(false);
      setSyncProgress(null);
      setTimeout(() => setSyncMessage(''), 6000);
    }
  };

  const prvHandleFiltroChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const prvAplicarFiltros = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setFiltrando(true);
      const response = await informeService.filtrarInformes(filtros.numero_siniestro, filtros.numero_poliza);
      setInformes(response.data);
    } catch (err: any) {
      console.error('Error al filtrar informes:', err);
      setError(err.response?.data?.error || 'Error al filtrar los informes');
    } finally {
      setFiltrando(false);
    }
  };

  const prvLimpiarFiltros = async () => {
    setFiltros({ numero_siniestro: '', numero_poliza: '' });
    await prvCargarInformes();
  };

  const prvMostrarConfirmacionEliminacion = (id: number, numero: string) => {
    setDeleteModal({ show: true, id, numero });
  };

  const prvCerrarModal = () => {
    setDeleteModal({ show: false, id: null, numero: '' });
  };

  const prvConfirmarEliminacion = async () => {
    try {
      setDeleting(true);
      if (deleteModal.id) {
        await informeService.eliminarInforme(deleteModal.id);
        setInformes(informes.filter(inf => inf.id !== deleteModal.id));
      }
      prvCerrarModal();
    } catch (err: any) {
      console.error('Error al eliminar informe:', err);
      setError(err.response?.data?.error || 'Error al eliminar el informe');
    } finally {
      setDeleting(false);
    }
  };

  const prvGetFaseLabel = (fase: string) => {
    const labels: Record<string, string> = {
      'encargo': 'Encargo',
      'cita': 'Cita',
      'videoperitacion': 'Videoperitación',
      'subidaArchivos': 'Archivos',
      'grabacion': 'Grabación Perito',
      'informe': 'Informe IA',
      'envio': 'Enviar',
      'cerrado': 'Cerrado'
    };
    return labels[fase] || fase;
  };

  const prvGetFaseColor = (fase: string) => {
    const colors: Record<string, string> = {
      'encargo': 'bg-gray-100 text-gray-800',
      'cita': 'bg-blue-100 text-blue-800',
      'videoperitacion': 'bg-purple-100 text-purple-800',
      'subidaArchivos': 'bg-pink-100 text-pink-800',
      'grabacion': 'bg-teal-100 text-teal-800',
      'informe': 'bg-indigo-100 text-indigo-800',
      'envio': 'bg-yellow-100 text-yellow-800',
      'cerrado': 'bg-green-100 text-green-800'
    };
    return colors[fase] || 'bg-gray-100';
  };

  const prvGetCompanyBadgeStyle = (compania: string) => {
    if (!compania || compania === '-') {
      return { backgroundColor: '#f1f5f9', color: '#64748b', borderColor: '#cbd5e1' };
    }
    let hash = 0;
    const str = compania.trim().toLowerCase();
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return {
      backgroundColor: `hsl(${hue}, 85%, 94%)`,
      color: `hsl(${hue}, 90%, 26%)`,
      borderColor: `hsl(${hue}, 65%, 78%)`,
    };
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Gestión de Informes</h1>
          <p className="subtitle">Vista general de expedientes activos</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={prvHandleSincronizarGmail} 
            disabled={syncingGmail}
          >
            {syncingGmail ? 'Sincronizando...' : <><FiInbox style={{marginRight: "4px"}} /> Obtener de Gmail</>}
          </button>
          <Link to="/informes/new/encargo" className="btn btn-primary">
            <FiPlus style={{marginRight: "4px"}} /> Nuevo Encargo
          </Link>
        </div>
      </div>

      <div className="content-card">
        <div className="table-toolbar">
          <form className="filtros-row" onSubmit={prvAplicarFiltros}>
            <div className="input-group">
              <span className="input-icon"><FiSearch /></span>
              <input
                type="text"
                name="numero_siniestro"
                className="input-search"
                placeholder="Buscar por Nº Siniestro..."
                value={filtros.numero_siniestro}
                onChange={prvHandleFiltroChange}
              />
            </div>
            <div className="input-group">
              <span className="input-icon"><FiFileText /></span>
              <input
                type="text"
                name="numero_poliza"
                className="input-search"
                placeholder="Buscar por Nº Póliza..."
                value={filtros.numero_poliza}
                onChange={prvHandleFiltroChange}
              />
            </div>
            <button type="submit" className="btn btn-secondary" disabled={filtrando}>
              {filtrando ? 'Buscando...' : 'Filtrar'}
            </button>
            {(filtros.numero_siniestro || filtros.numero_poliza) && (
              <button type="button" onClick={prvLimpiarFiltros} className="btn-ghost">
                Limpiar
              </button>
            )}
          </form>
        </div>

        {syncingGmail && syncProgress && (
          <div style={{
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            color: '#1e3a8a',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '14px' }}>
              <span>Sincronizando correos con Gmail...</span>
              <span style={{
                backgroundColor: '#2563eb',
                color: '#ffffff',
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontFamily: 'monospace',
                fontWeight: 'bold'
              }}>
                {syncProgress.total > 0 ? `${syncProgress.current} / ${syncProgress.total} (${Math.round((syncProgress.current / syncProgress.total) * 100)}%)` : 'Conectando...'}
              </span>
            </div>

            {syncProgress.total > 0 && (
              <div style={{ width: '100%', backgroundColor: '#dbeafe', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{
                  backgroundColor: '#2563eb',
                  height: '100%',
                  width: `${Math.min(100, Math.round((syncProgress.current / syncProgress.total) * 100))}%`,
                  transition: 'width 0.3s ease-out'
                }} />
              </div>
            )}

            <div style={{ fontSize: '12px', color: '#1d4ed8', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {syncProgress.message}
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        {syncMessage && <div className="success-message" style={{ background: '#d4edda', color: '#155724', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>{syncMessage}</div>}

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Cargando informes...</p>
          </div>
        ) : informes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><FiFolder /></div>
            <h3>No hay informes registrados</h3>
            <p>Comienza creando un nuevo encargo pericial.</p>
            <Link to="/informes/new/encargo" className="btn btn-secondary">
              Crear primer informe
            </Link>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nº Informe</th>
                  <th>Compañía</th>
                  <th>Nº Póliza</th>
                  <th>Nº Siniestro</th>
                  <th>F. Efecto</th>
                  <th>F. Ocurrencia</th>
                  <th>Asegurado</th>
                  <th>Fase</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {informes.map(informe => (
                  <tr key={informe.id} className="table-row">
                    <td className="font-medium text-primary">
                      <Link to={`/informes/${informe.id}/${informe.fase || 'encargo'}`}>
                        {informe.numero_expediente}
                      </Link>
                    </td>
                    <td>
                      <span 
                        className="badge-company"
                        style={prvGetCompanyBadgeStyle(informe.compania)}
                      >
                        {informe.compania || '-'}
                      </span>
                    </td>
                    <td>{informe.numero_poliza}</td>
                    <td>{informe.numero_siniestro}</td>
                    <td>{informe.fecha_efecto}</td>
                    <td>{informe.fecha_siniestro}</td>
                    <td>
                      <div className="user-cell">
                        <span className="font-medium">{informe.nombre} {informe.apellidos}</span>
                        <span className="text-xs text-muted">{informe.poblacion}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${prvGetFaseColor(informe.fase)}`}>
                        {prvGetFaseLabel(informe.fase)}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="actions-cell">
                        <button
                          className="btn-icon delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            prvMostrarConfirmacionEliminacion(informe.id, informe.numero_expediente || informe.numero_siniestro || '');
                          }}
                          title="Eliminar Expediente"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Eliminar Expediente</h3>
            <p>
              ¿Estás seguro de que deseas eliminar el expediente <strong>{deleteModal.numero}</strong>?
              <br />
              Esta acción no se puede deshacer.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={prvCerrarModal}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                onClick={prvConfirmarEliminacion}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InformesList;
